// Merchant Sales Report (MM-3037): the Filter button and its "Reporting Period" panel (Figma 3071:19832):
// Yesterday / Last 3 Days / Last 7 Days, From / To, "N days selected", Reset / Apply. Ranges are capped at
// 30 days (ticket) and end no later than the snapshot's data_as_of.
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import ButtonWithImg from "../../../components/buttons/ButtonWithImg";
import CustomDatePicker from "../../../components/DatePicker/CustomDatePicker";
import ErrorMessage from "../../../components/ErrorMessages/ErrorMessage";
import {
  API_DATE_FORMAT,
  defaultRange,
  formatDisplayDate,
  MAX_RANGE_DAYS,
  matchingPreset,
  PRESETS,
  presetRange,
  rangeDays,
  validateRange,
} from "./dateRange";
import { DateRange } from "./types";

const FIELD_DATE_FORMAT = "DD/MM/YYYY";

interface ReportingPeriodFilterProps {
  dataAsOf: string;
  value: DateRange;
  onApply: (range: DateRange) => void;
  disabled?: boolean;
}

const ReportingPeriodFilter = ({ dataAsOf, value, onApply, disabled }: ReportingPeriodFilterProps) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<string | null>(value.start);
  const [end, setEnd] = useState<string | null>(value.end);
  const panelRef = useRef<HTMLDivElement>(null);

  // Opening the panel always starts from the applied range.
  useEffect(() => {
    if (open) {
      setStart(value.start);
      setEnd(value.end);
    }
  }, [open, value.start, value.end]);

  // Close on a click outside. The date picker's calendar renders in a portal, so clicks inside it are ignored.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (panelRef.current?.contains(target) || target.closest?.(".ant-picker-dropdown")) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const error = validateRange(start, end, dataAsOf);
  const selectedDays = start && end ? rangeDays(start, end) : 0;
  const activePreset = start && end ? matchingPreset({ start, end }, dataAsOf) : null;
  const isDefault = (() => {
    const fallback = defaultRange(dataAsOf);
    return value.start === fallback.start && value.end === fallback.end;
  })();
  const afterDataAsOf = (current: dayjs.Dayjs) => current.isAfter(dayjs(dataAsOf), "day");

  const apply = () => {
    if (error || !start || !end) return;
    onApply({ start, end });
    setOpen(false);
  };

  const reset = () => {
    const fallback = defaultRange(dataAsOf);
    setStart(fallback.start);
    setEnd(fallback.end);
  };

  // Figma "Pop up Button Secondary" / "Pop up Button Primary".
  const secondaryButton = "h-8 rounded-lg border border-p_primary bg-white px-4 text-xs font-semibold text-p_primary";
  const primaryButton = "h-8 rounded-lg border border-p_primary bg-p_primary px-4 text-xs font-semibold text-white";

  return (
    <div className="relative" ref={panelRef}>
      {/* Same Filter button as the other listing pages (DynamicFilterPanel). */}
      <ButtonWithImg
        buttonName="Filter"
        ImgSrc="filter"
        className="px-4 disabled:border-disable_stroke disabled:text-disable_text"
        buttonOnclick={() => setOpen((v) => !v)}
        disabledButton={disabled}
        ui={
          !isDefault && (
            <span className="flex size-5 items-center justify-center rounded-full bg-p_light text-xs" data-testid="merchant-sales-filter-count">
              1
            </span>
          )
        }
      />
      {open && (
        <div
          role="dialog"
          aria-label="Reporting Period"
          className="absolute right-0 top-full z-30 mt-2 flex w-[340px] flex-col gap-4 rounded-lg border border-[#dde2ea] bg-[#fefefe] p-4 shadow-[0_8px_16px_rgba(0,0,0,0.05)]"
        >
          <div className="flex flex-col gap-1 leading-[1.4]">
            <div className="text-[13px] font-semibold text-[#1d2433]">Reporting Period</div>
            <div className="text-[10px] text-[#6b7385]">
              Up to {MAX_RANGE_DAYS} days · data available until {formatDisplayDate(dataAsOf)}
            </div>
          </div>
          <div className="flex items-center justify-between">
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={activePreset === preset.key ? primaryButton : secondaryButton}
                onClick={() => {
                  const range = presetRange(preset.days, dataAsOf);
                  setStart(range.start);
                  setEnd(range.end);
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2.5 text-[10px] font-medium leading-[1.4] text-primary_text">
              <label htmlFor="merchant-sales-from">From</label>
              <label htmlFor="merchant-sales-to">To</label>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <CustomDatePicker
                testID="merchant-sales-from"
                id="merchant-sales-from"
                label="From"
                hideLabel
                type="start"
                classType="period"
                format={FIELD_DATE_FORMAT}
                iconSrc="calendar-outline-blue"
                required={false}
                disabled={false}
                value={start ? dayjs(start) : null}
                disabledDateFn={afterDataAsOf}
                handleStates={(date) => setStart(date ? date.format(API_DATE_FORMAT) : null)}
              />
              <CustomDatePicker
                testID="merchant-sales-to"
                id="merchant-sales-to"
                label="To"
                hideLabel
                type="end"
                classType="period"
                format={FIELD_DATE_FORMAT}
                iconSrc="calendar-outline-blue"
                required={false}
                disabled={false}
                value={end ? dayjs(end) : null}
                disabledDateFn={afterDataAsOf}
                handleStates={(date) => setEnd(date ? date.format(API_DATE_FORMAT) : null)}
              />
            </div>
            {selectedDays > 0 && (
              <div className="text-[10px] leading-[1.4] text-[#6b7385]">
                {selectedDays} {selectedDays === 1 ? "day" : "days"} selected
              </div>
            )}
            {error && <ErrorMessage errorMessage={error} testId="merchant-sales-period-error" />}
          </div>
          <div className="flex justify-end gap-1.5">
            <button type="button" className={secondaryButton} onClick={reset}>
              Reset
            </button>
            <button
              type="button"
              className={`${primaryButton} disabled:border-disable_stroke disabled:bg-disable_stroke disabled:text-disable_text`}
              onClick={apply}
              disabled={!!error}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportingPeriodFilter;
